import { Option, Result } from 'effect'

export type StateTransformer<T> = (previous: T) => T
export type ValueOrTransformer<T> = T | StateTransformer<T>
export type StateDispatcher<T> = (action: ValueOrTransformer<T>) => void

export const bindDispatcher =
  <T>(dispatcher: StateDispatcher<T>) =>
  <Arguments extends unknown[]>(action: (...arguments_: Arguments) => ValueOrTransformer<T>) =>
  (...arguments_: Arguments): void => {
    dispatcher(action(...arguments_))
  }

// これで汎用化はできるはずだが実例が出てからまた考える
/**
 * アクションの結果を受け取って処理する関数（Executor）を作成する高階関数
 *
 * @template Payload アクションが生成する値（StateUpdaterなど）の型
 * @template Return Executorが返す値の型（void, Promise<void>, booleanなど）
 */
export const createBinder =
  <Payload, Return>(executor: (payload: Payload) => Return) =>
  <Arguments extends unknown[]>(action: (...arguments_: Arguments) => Payload) =>
  (...arguments_: Arguments): Return =>
    executor(action(...arguments_))
// const bindDispatcher = createBinder(dispatcher)

/**
 * 任意のモナド的構造（Option, Resultなど）から値を取り出して適用するための戦略関数
 */
type UnwrapStrategy<Container, Inner, ErrorType> = (
  container: Container,
  onSuccess: (value: Inner) => void,
  onFailure?: (error: ErrorType) => void,
) => void

/**
 * 汎用バインダーを作成するファクトリ
 * 「箱の開け方（strategy）」を渡すことで、Option用やResult用のバインダーを生成できる
 */
export const createGenericBinder =
  <Container, Inner, ErrorType = never>(
    strategy: UnwrapStrategy<Container, Inner, ErrorType>,
    onFailure?: (error: ErrorType) => void,
  ) =>
  (dispatcher: StateDispatcher<Inner>) =>
  <Arguments extends unknown[]>(action: (...arguments_: Arguments) => Container) =>
  (...arguments_: Arguments): void => {
    const result = action(...arguments_)
    strategy(
      result,
      (value) => {
        dispatcher(value)
      },
      onFailure,
    )
  }

/*
 * 使用例: Option用のバインダーを定義する場合
 */
const optionStrategy = <T>(option: Option.Option<T>, onSuccess: (value: T) => void): void => {
  if (Option.isSome(option)) {
    onSuccess(option.value)
  }
}
export const bindOptionDispatcher = <T>(dispatcher: StateDispatcher<T>) =>
  createGenericBinder<Option.Option<T>, T>(optionStrategy)(dispatcher)

/*
 * 使用例: Result用のバインダー
 * createGenericBinderの第2引数としてエラーハンドラを渡す形になる
 */
const resultStrategy = <T, E>(
  result: Result.Result<T, E>,
  onSuccess: (value: T) => void,
  onFailure?: (error: E) => void,
): void => {
  if (Result.isSuccess(result)) {
    onSuccess(result.success)
  } else if (onFailure !== undefined && Result.isFailure(result)) {
    onFailure(result.failure)
  }
}
export const bindResultDispatcher = <T, E>(dispatcher: StateDispatcher<T>, onError?: (error: E) => void) =>
  createGenericBinder<Result.Result<T, E>, T, E>(resultStrategy, onError)(dispatcher)

// 純粋な値（Identity）用のStrategy。単にnextを呼ぶだけ。
const identityStrategy = <T>(value: T, onSuccess: (value: T) => void): void => {
  onSuccess(value)
}
export const bindPureDispatcher = <T>(dispatcher: StateDispatcher<T>) =>
  createGenericBinder<T, T>(identityStrategy)(dispatcher)
